// Inspired by Resumable.js (https://github.com/23/resumable.js)

import type { ResumableChunk } from "./resumable_chunk";
import { ResumableFile } from "./resumable_file";
import { formatSize, generateUniqueIdentifier } from "./resumable_shared";

/**
 * Configuration options for the Resumable instance.
 */
export interface ConfigurationHash {
  /**
   * The size in bytes of each uploaded chunk of data. The last uploaded chunk will be at least this size and up to two the size. (Default: `1*1024*1024`)
   */
  chunkSize?: number;
  /**
   * Force all chunks to be less or equal than chunkSize. Otherwise, the last chunk will be greater than or equal to `chunkSize`. (Default: `false`)
   */
  forceChunkSize?: boolean;
  /**
   * Number of simultaneous uploads (Default: `3`)
   */
  simultaneousUploads?: number;
  /**
   * The name of the multipart request parameter to use for the file chunk (Default: `file`)
   */
  fileParameterName?: string;
  /**
   * The name of the chunk index (base-1) in the current upload POST parameter to use for the file chunk (Default: `resumableChunkNumber`)
   */
  chunkNumberParameterName?: string;
  /**
   * The name of the general chunk size POST parameter to use for the file chunk (Default: `resumableChunkSize`)
   */
  chunkSizeParameterName?: string;
  /**
   * The name of the current chunk size POST parameter to use for the file chunk (Default: `resumableCurrentChunkSize`)
   */
  currentChunkSizeParameterName?: string;
  /**
   * The name of the total file size number POST parameter to use for the file chunk (Default: `resumableTotalSize`)
   */
  totalSizeParameterName?: string;
  /**
   * The name of the file type POST parameter to use for the file chunk (Default: `resumableType`)
   */
  typeParameterName?: string;
  /**
   * The name of the unique identifier POST parameter to use for the file chunk (Default: `resumableIdentifier`)
   */
  identifierParameterName?: string;
  /**
   * The name of the original file name POST parameter to use for the file chunk (Default: `resumableFilename`)
   */
  fileNameParameterName?: string;
  /**
   * The name of the file's relative path POST parameter to use for the file chunk (Default: `resumableRelativePath`)
   */
  relativePathParameterName?: string;
  /**
   * The name of the total number of chunks POST parameter to use for the file chunk (Default: `resumableTotalChunks`)
   */
  totalChunksParameterName?: string;
  throttleProgressCallbacks?: number;
  /**
   * Extra parameters to include in the multipart request with data. This can be an object or a function. If a function, it will be passed a ResumableFile and a ResumableChunk object (Default: `{}`)
   */
  query?:
    | Record<string, any>
    | ((file: ResumableFile, chunk?: ResumableChunk) => Record<string, any>);
  /**
   * Extra headers to include in the multipart POST with data. This can be an `object` or a `function` that allows you to construct and return a value, based on supplied `file` (Default: `{}`)
   */
  headers?:
    | Record<string, string>
    | ((file: ResumableFile, chunk?: ResumableChunk) => Record<string, string>);
  /**
   * Prioritize first and last chunks of all files. This can be handy if you can determine if a file is valid for your service from only the first or last chunk. For example, photo or video meta data is usually located in the first part of a file, making it easy to test support from only the first chunk. (Default: `false`)
   */
  prioritizeFirstAndLastChunk?: boolean;
  /**
   * The target URL for the multipart POST request. This can be a `string`. (Default: `/`)
   */
  target?: string;
  /**
   * Make a GET request to the server for each chunks to see if it already exists. If implemented on the server-side, this will allow for upload resumes even after a browser crash or even a computer restart. (Default: `true`)
   */
  testChunks?: boolean;
  /**
   * Override the function that generates unique identifiers for each file.
   */
  generateUniqueIdentifier?: ((file: File, event?: Event) => string | Promise<string>) | null;
  /**
   * The maximum number of retries for a chunk before the upload is failed. Valid values are any positive integer and `undefined` for no limit. (Default: `undefined`)
   */
  maxChunkRetries?: number;
  /**
   * The number of milliseconds to wait before retrying a chunk on a non-permanent error. Valid values are any positive integer and `undefined` for immediate retry. (Default: `undefined`)
   */
  chunkRetryInterval?: number | null;
  /**
   * List of HTTP status codes that define if the chunk upload was a permanent error and should not retry the upload. (Default: `[400, 404, 409, 415, 500, 501]`)
   */
  permanentErrors?: number[];
  /**
   * Indicates how many files can be uploaded in a single session. Valid values are any positive integer and `undefined` for no limit. (Default: `undefined`)
   */
  maxFiles?: number;
  /**
   * Standard CORS requests do not send or set any cookies by default. In order to include cookies as part of the request, you need to set the `withCredentials` property to true. (Default: `false`)
   */
  withCredentials?: boolean;
  /**
   * The timeout in milliseconds for each request (Default: `0`)
   */
  fetchTimeout?: number;
  clearInput?: boolean;
  chunkFormat?: "blob" | "base64";
  /**
   * Set chunk content-type from original file.type. (Default: `false`, if `false` default Content-Type: `application/octet-stream`)
   */
  setChunkTypeFromFile?: boolean;
  /**
   * A function which displays the *please upload n file(s) at a time* message. (Default: displays an alert box with the message *Please n one file(s) at a time.*)
   */
  maxFilesErrorCallback?: (files: File[], errorCount: number) => void;
  /**
   * The minimum allowed file size. (Default: 1)
   */
  minFileSize?: number;
  /**
   * A function which displays an error a selected file is smaller than allowed. (Default: displays an alert for every bad file.)
   */
  minFileSizeErrorCallback?: (file: File, errorCount: number) => void;
  /**
   * The maximum allowed file size. (Default: `undefined`)
   */
  maxFileSize?: number;
  /**
   * A function which displays an error a selected file is larger than allowed. (Default: displays an alert for every bad file.)
   */
  maxFileSizeErrorCallback?: (file: File, errorCount: number) => void;
  /**
   * The file types allowed to upload. An empty array allow any file type. (Default: `[]`)
   */
  fileType?: string[];
  /**
   * A function which displays an error a selected file has type not allowed. (Default: displays an alert for every bad file.)
   */
  fileTypeErrorCallback?: (file: File, errorCount: number) => void;
}

interface ExtendedFile extends File {
  relativePath?: string;
  uniqueIdentifier?: string;
}

export type ResumableEventDetail =
  | { type: "chunkingStart" | "chunkingComplete" | "fileRetry"; file: ResumableFile }
  | { type: "chunkingProgress" | "terminalFileError"; file: ResumableFile; message: string }
  | { type: "fileProgress" | "fileError" | "fileSuccess"; file: ResumableFile; message?: string }
  | { type: "filesAdded"; files: ResumableFile[]; skippedFiles?: File[] }
  | { type: "fileAdded"; file: ResumableFile; event: Event }
  | { type: "error"; error?: any; file?: ResumableFile }
  | { type: "progress" | "uploadStart" | "pause" | "cancel" | "beforeCancel" | "beforeAdd" }
  | { type: "complete"; didUploadCompleteSuccessfully?: boolean };

export class ResumableUploadEvent extends CustomEvent<ResumableEventDetail> {}

function isFileSystemDirectoryEntry(
  entry: FileSystemEntry | null | undefined,
): entry is FileSystemDirectoryEntry {
  // biome-ignore lint/complexity/useOptionalChain: Optional chain does not return boolean but boolean | undefined
  return entry != null && entry.isDirectory;
}

function isFileSystemFileEntry(
  entry: FileSystemEntry | null | undefined,
): entry is FileSystemFileEntry {
  // biome-ignore lint/complexity/useOptionalChain: Optional chain does not return boolean but boolean | undefined
  return entry != null && entry.isFile;
}

/**
 * Main class for managing resumable uploads.
 * Implements EventTarget via composition to ensure compatibility.
 */
export class ResumableUpload implements EventTarget {
  /**
   * An array of `ResumableFile` file objects added by the user.
   */
  files: ResumableFile[] = [];
  defaults: Required<ConfigurationHash>;
  /**
   * A hash object of the configuration of the Resumable.js instance.
   */
  opts: Partial<ConfigurationHash>;
  version = 1.0;

  private _eventTarget: EventTarget;
  private _completeDispatched = false;

  constructor(opts: Partial<ConfigurationHash> = {}) {
    this._eventTarget = new EventTarget();

    this.defaults = {
      chunkSize: 1 * 1024 * 1024,
      forceChunkSize: false,
      simultaneousUploads: 3,
      fileParameterName: "file",
      chunkNumberParameterName: "resumableChunkNumber",
      chunkSizeParameterName: "resumableChunkSize",
      currentChunkSizeParameterName: "resumableCurrentChunkSize",
      totalSizeParameterName: "resumableTotalSize",
      typeParameterName: "resumableType",
      identifierParameterName: "resumableIdentifier",
      fileNameParameterName: "resumableFilename",
      relativePathParameterName: "resumableRelativePath",
      totalChunksParameterName: "resumableTotalChunks",
      throttleProgressCallbacks: 0.5,
      query: {},
      headers: {},
      prioritizeFirstAndLastChunk: false,
      target: "/",
      testChunks: true,
      generateUniqueIdentifier: null,
      maxChunkRetries: 100,
      chunkRetryInterval: null,
      permanentErrors: [400, 401, 403, 404, 409, 415, 500, 501],
      maxFiles: Number.POSITIVE_INFINITY,
      withCredentials: false,
      fetchTimeout: 0,
      clearInput: true,
      chunkFormat: "blob",
      setChunkTypeFromFile: false,
      maxFilesErrorCallback: () => {
        const maxFiles = this.getOpt("maxFiles");
        console.error(
          `Please upload no more than ${maxFiles} file${maxFiles === 1 ? "" : "s"} at a time.`,
        );
      },
      minFileSize: 1,
      minFileSizeErrorCallback: (file: File) => {
        console.error(
          `${file.name} is too small, please upload files larger than ${formatSize(this.getOpt("minFileSize"))}.`,
        );
      },
      maxFileSize: Number.POSITIVE_INFINITY,
      maxFileSizeErrorCallback: (file: File) => {
        console.error(
          `${file.name} is too large, please upload files less than ${formatSize(this.getOpt("maxFileSize"))}.`,
        );
      },
      fileType: [],
      fileTypeErrorCallback: (file: File) => {
        console.error(
          `${file.name} has type not allowed, please upload files of type ${this.getOpt("fileType")}.`,
        );
      },
    };

    this.opts = opts;
  }

  /**
   * Listen for event from Resumable
   */
  addEventListener<K extends Event = Event>(
    type: string,
    callback: ((event: K) => void) | EventListenerObject | null,
    options?: boolean | AddEventListenerOptions,
  ): void {
    this._eventTarget.addEventListener(
      type,
      callback as EventListenerOrEventListenerObject | null,
      options,
    );
  }

  dispatchEvent(event: Event): boolean {
    return this._eventTarget.dispatchEvent(event);
  }

  removeEventListener(
    type: string,
    callback: EventListener | null,
    options?: boolean | EventListenerOptions | undefined,
  ): void {
    this._eventTarget.removeEventListener(type, callback, options);
  }

  getOpts<T extends keyof ConfigurationHash>(
    options: T[],
  ): { [K in T]: ReturnType<typeof this.getOpt<K>> } {
    return options.reduce(
      (acc, opt) => {
        acc[opt] = this.getOpt(opt);
        return acc;
      },
      {} as { [K in T]: ReturnType<typeof this.getOpt<K>> },
    );
  }

  getOpt<T extends keyof ConfigurationHash>(option: T): Required<ConfigurationHash>[T] {
    const value = this.opts[option];
    return (value !== undefined ? value : this.defaults[option]) as Required<ConfigurationHash>[T];
  }

  /*
   * Prefer `dispatchFromDetail` which offers better typing as the eventName and eventDetail.type
   * properties cannot conflict.
   */
  dispatch(eventName: string, detail: ResumableEventDetail = { type: "progress" }): void {
    const event = new ResumableUploadEvent(eventName, { detail });

    this.dispatchEvent(event);

    // In case of file-scoped `fileError` or `fileProgress` events, we also dispatch
    // the upload-scoped `error` and `progress` events.
    if (eventName === "fileError") {
      this.dispatchFromDetail({
        type: "error",
        error: "message" in detail ? detail.message : undefined,
        file: "file" in detail ? detail.file : undefined,
      });
    }
    if (eventName === "fileProgress") {
      this.dispatchFromDetail({ type: "progress" });
    }
  }

  dispatchFromDetail(detail: ResumableEventDetail): void {
    this.dispatch(detail.type, detail);
  }
  /**
   * processes a single upload item (file or directory)
   * @param item item to upload, may be file or directory entry
   * @param path current file path
   * @param items list of files to append new items to
   * @param callback callback invoked when item is processed
   */
  private processItem(
    item: FileSystemEntry,
    path: string,
    items: ExtendedFile[],
    callback: () => void,
  ): void {
    let entry: FileSystemEntry | null = null;

    if (isFileSystemFileEntry(item)) {
      item.file((file: File) => {
        (file as ExtendedFile).relativePath = path + file.name;
        items.push(file as ExtendedFile);
        callback();
      });
      return;
    } else if (isFileSystemDirectoryEntry(item)) {
      entry = item;
    } else if (item instanceof File) {
      items.push(item);
    }

    if ("webkitGetAsEntry" in item && typeof item.webkitGetAsEntry === "function") {
      entry = item.webkitGetAsEntry();
    }

    if (isFileSystemDirectoryEntry(entry)) {
      // directory provided, process it
      this.processDirectory(entry, path + entry.name + "/", items, callback);
      return;
    }

    if ("getAsFile" in item && typeof item.getAsFile === "function") {
      // item represents a File object, convert it
      const file = item.getAsFile();
      if (file instanceof File) {
        (file as ExtendedFile).relativePath = path + file.name;
        items.push(file);
      }
    }
    callback(); // indicate processing is done
  }

  /**
   * cps-style list iteration.
   * invokes all functions in list and waits for their callback to be
   * triggered.
   * @param items list of functions expecting callback parameter
   * @param callback callback to trigger after the last callback has been invoked
   */
  private processCallbacks(
    items: Array<(callback: () => void) => void>,
    callback: () => void,
  ): void {
    if (!items || items.length === 0) {
      // empty or no list, invoke callback
      callback();
      return;
    }
    // invoke current function, pass the next part as continuation
    items[0](() => {
      this.processCallbacks(items.slice(1), callback);
    });
  }

  /**
   * recursively traverse directory and collect files to upload
   * @param directory directory to process
   * @param path current path
   * @param items target list of items
   * @param callback callback invoked after traversing directory
   */
  private processDirectory(
    directory: FileSystemDirectoryEntry,
    path: string,
    items: ExtendedFile[],
    callback: () => void,
  ): void {
    const dirReader = directory.createReader();
    const allEntries: FileSystemEntry[] = [];
    const readEntries = (): void => {
      dirReader.readEntries((entries: FileSystemEntry[]) => {
        if (entries.length) {
          allEntries.push(...entries);
          return readEntries();
        }

        // process all conversion callbacks, finally invoke own one
        this.processCallbacks(
          allEntries.map((entry) => this.processItem.bind(this, entry, path, items)),
          callback,
        );
      });
    };

    readEntries();
  }

  /**
   * append files from a file list
   * @param fileList list of files to append
   * @param event event that triggered the file append
   */
  private appendFilesFromFileList(fileList: File[], event: Event): void {
    // check for uploading too many files
    let errorCount = 0;
    const options = this.getOpts([
      "maxFiles",
      "minFileSize",
      "maxFileSize",
      "maxFilesErrorCallback",
      "minFileSizeErrorCallback",
      "maxFileSizeErrorCallback",
      "fileType",
      "fileTypeErrorCallback",
    ]);

    if ((options.maxFiles as number) < fileList.length + this.files.length) {
      if (options.maxFiles === 1 && this.files.length === 1 && fileList.length === 1) {
        this.removeFile(this.files[0]);
      } else {
        (options.maxFilesErrorCallback as (files: File[], errorCount: number) => void)(
          fileList,
          errorCount++,
        );
        return;
      }
    }

    const files: ResumableFile[] = [];
    const filesSkipped: File[] = [];
    let remaining = fileList.length;

    const decreaseRemaining = (): void => {
      if (!--remaining) {
        // all files processed, trigger event
        if (!files.length && !filesSkipped.length)
          // no succeeded files, just skip
          return;
        setTimeout(() => {
          this.dispatchFromDetail({
            type: "filesAdded",
            files: files,
            skippedFiles: filesSkipped,
          });
        }, 0);
      }
    };

    for (const file of fileList) {
      const fileName = file.name;
      const fileType = file.type;

      if (options.fileType.length > 0) {
        let fileTypeFound = false;
        for (const index in options.fileType) {
          // For good behaviour we do some initial sanitizing. Remove spaces and lowercase all
          const typeDef = options.fileType[index].replace(/\s/g, "").toLowerCase();
          // Allowing for both [extension, .extension, mime/type, mime/*]
          let extension = typeDef.match(/^[^.][^/]+$/) ? "." + typeDef : typeDef;

          if (
            fileName.substr(-1 * extension.length).toLowerCase() === extension ||
            //If MIME type, check for wildcard or if extension matches the files tiletype
            (extension.indexOf("/") !== -1 &&
              ((extension.indexOf("*") !== -1 &&
                fileType.substr(0, extension.indexOf("*")) ===
                  extension.substr(0, extension.indexOf("*"))) ||
                fileType === extension))
          ) {
            fileTypeFound = true;
            break;
          }
        }
        if (!fileTypeFound) {
          options.fileTypeErrorCallback(file, errorCount++);
          continue;
        }
      }

      if (file.size < (options.minFileSize as number)) {
        (options.minFileSizeErrorCallback as (file: File, errorCount: number) => void)(
          file,
          errorCount++,
        );
        continue;
      }
      if (file.size > (options.maxFileSize as number)) {
        (options.maxFileSizeErrorCallback as (file: File, errorCount: number) => void)(
          file,
          errorCount++,
        );
        continue;
      }

      const addFile = (uniqueIdentifier: string): void => {
        if (!this.getFromUniqueIdentifier(uniqueIdentifier)) {
          (file as ExtendedFile).uniqueIdentifier = uniqueIdentifier;
          const f = new ResumableFile(this, file, uniqueIdentifier);
          this.files.push(f);
          files.push(f);
          f.container = typeof event !== "undefined" ? event.target : null;
          setTimeout(() => {
            this.dispatchFromDetail({ type: "fileAdded", file: f, event });
          }, 0);
        } else {
          filesSkipped.push(file);
        }
        decreaseRemaining();
      };

      // directories have size == 0
      const uniqueIdentifier = generateUniqueIdentifier(file, event);
      const customGenerator = this.getOpt("generateUniqueIdentifier");

      if (typeof customGenerator === "function") {
        const result = customGenerator(file, event);
        if (result && typeof (result as any).then === "function") {
          // Promise or Promise-like object provided as unique identifier
          (result as Promise<string>).then(addFile).catch(decreaseRemaining);
        } else {
          // non-Promise provided as unique identifier, process synchronously
          addFile(result as string);
        }
      } else {
        addFile(uniqueIdentifier);
      }
    }
  }

  uploadNextChunk(): boolean {
    let chunkFound = false;

    // In some cases (such as videos) it's really handy to upload the first
    // and last chunk of a file quickly; this let's the server check the file's
    // metadata and determine if there's even a point in continuing.
    if (this.getOpt("prioritizeFirstAndLastChunk")) {
      for (const file of this.files) {
        if (file.chunks.length && file.chunks[0].status() === "pending") {
          file.chunks[0].send();
          return true;
        }
        if (file.chunks.length > 1 && file.chunks[file.chunks.length - 1].status() === "pending") {
          file.chunks[file.chunks.length - 1].send();
          return true;
        }
      }
    }

    // Now, simply look for the next, best thing to upload
    for (const file of this.files) {
      chunkFound = file.upload();
      if (chunkFound) return true;
    }

    // The are no more outstanding chunks to upload, check is everything is done
    const outstanding = this.files.some((file) => !file.isComplete());
    if (!outstanding && !this._completeDispatched) {
      this._completeDispatched = true;
      this.dispatchFromDetail({
        type: "complete",
        didUploadCompleteSuccessfully: this.files.every((file) => !file.hasError()),
      });
    }

    return false;
  }

  /**
   * Returns a boolean indicating whether or not the instance is currently uploading anything.
   */
  isUploading(): boolean {
    return this.files.some((file) => file.isUploading());
  }

  /**
   * Start or resume uploading.
   */
  upload(): void {
    // Make sure we don't start too many uploads at once
    if (this.isUploading()) return;

    for (const file of this.files) {
      file.pause(false);
    }

    // Kick off the queue
    this._completeDispatched = false;
    this.dispatchFromDetail({ type: "uploadStart" });
    for (let num = 1; num <= (this.getOpt("simultaneousUploads") as number); num++) {
      this.uploadNextChunk();
    }
  }

  waitForComplete() {
    return new Promise<void>((resolve) =>
      this.addEventListener("complete", () => resolve(), { once: true }),
    );
  }

  /**
   * Pause uploading.
   */
  pause(): void {
    for (const file of this.files) {
      file.abort();
      file.pause(true);
    }
    this.dispatchFromDetail({ type: "pause" });
  }

  /**
   * Cancel upload of all `ResumableFile` objects and remove them from the list.
   */
  cancel(): void {
    this.dispatchFromDetail({ type: "beforeCancel" });
    for (let i = this.files.length - 1; i >= 0; i--) this.files[i].cancel();
    this.dispatchFromDetail({ type: "cancel" });
  }

  /**
   * Returns a float between 0 and 1 indicating the current upload progress of all files.
   */
  progress(): number {
    let totalDone = 0;
    let totalSize = 0;
    for (const file of this.files) {
      totalDone += file.progress() * file.size;
      totalSize += file.size;
    }
    return totalSize > 0 ? totalDone / totalSize : 0;
  }

  /**
   * Add a HTML5 File object to the list of files.
   */
  addFile(file: File, event?: Event): void {
    this.dispatchFromDetail({ type: "beforeAdd" });
    this.appendFilesFromFileList([file], event || new Event("addFile"));
  }

  /**
   * Add an Array of HTML5 File objects to the list of files.
   */
  addFiles(files: File[], event?: Event): void {
    this.dispatchFromDetail({ type: "beforeAdd" });
    this.appendFilesFromFileList(files, event || new Event("addFiles"));
  }

  /**
   * Remove a specific `ResumableFile` object from the list.
   */
  removeFile(file: ResumableFile): void {
    for (let i = this.files.length - 1; i >= 0; i--) {
      if (this.files[i] === file) this.files.splice(i, 1);
    }
  }

  /**
   * Look up a `ResumableFile` object by its unique identifier.
   */
  getFromUniqueIdentifier(uniqueIdentifier: string): ResumableFile | false {
    return this.files.find((file) => file.uniqueIdentifier === uniqueIdentifier) || false;
  }

  /**
   * Returns the total size of the upload in bytes.
   */
  getSize(): number {
    let totalSize = 0;
    for (const file of this.files) totalSize += file.size;
    return totalSize;
  }

  updateQuery(query: Record<string, any>): void {
    this.opts.query = query;
  }
}

export default ResumableUpload;
